"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useLocation } from "react-router-dom";
import FloatingChatbot from "../index";
import { AgentSidePanel } from "../components/agent-side-panel";
import { ChatSlotsProvider, type ChatSlots } from "../slots";
import { useActiveRoutes } from "@/core/routes/useActiveRoutes";
import { useTrackBrunaOpened } from "../hooks/useTrackBrunaOpened";
import type { Message } from "../types";

/**
 * Superfície que hospeda o chat (PED-12262):
 * - "floating": bolha + janela flutuante (shell legado).
 * - "docked": painel lateral fixo à direita do conteúdo (shell WOS, Fase 3).
 */
export type ChatbotHost = "floating" | "docked";

import { markScrolledToStart } from "@/core/utils/chatbot-storage";
import { saveChatState } from "@/core/utils/chatbot-storage";
import type { AgentType } from "../types";

export type PendingChatMessage = {
  messageToSend: string;
  displayText?: string;
  /** Anexo a renderizar na bolha do usuário (ex.: card do arquivo enviado). */
  attachment?: { fileName: string; fileSize: number };
};

type ChatbotContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  openWithMessage: (
    messageToSend: string,
    displayText?: string,
    attachment?: { fileName: string; fileSize: number },
  ) => void;
  pendingMessage: PendingChatMessage | null;
  clearPendingMessage: () => void;
  /** Atualiza a lista de mensagens do chat (registrado pelo FloatingChatbot). */
  updateChatMessages: Dispatch<SetStateAction<Message[]>> | null;
  registerChatMessagesUpdater: (
    updater: Dispatch<SetStateAction<Message[]>> | null,
  ) => void;
  activeAgent: AgentType | null;
  setActiveAgent: (agent: AgentType | null) => void;
  forcedAgent: AgentType | null;
  setForcedAgent: (agent: AgentType | null) => void;
};

const ChatbotContext = createContext<ChatbotContextValue | null>(null);

/** Valor padrão quando o hook é usado fora do provider (ex.: durante HMR). Evita crash e permite hot reload. */
const DEFAULT_CHATBOT_CONTEXT: ChatbotContextValue = {
  isOpen: false,
  open: () => {},
  close: () => {},
  toggle: () => {},
  openWithMessage: () => {},
  pendingMessage: null,
  clearPendingMessage: () => {},
  updateChatMessages: null,
  registerChatMessagesUpdater: () => {},
  activeAgent: null,
  setActiveAgent: () => {},
  forcedAgent: null,
  setForcedAgent: () => {},
};

export function ChatbotProvider({
  children,
  host = "floating",
  slots,
}: {
  children: React.ReactNode;
  host?: ChatbotHost;
  /**
   * Componentes de feature renderizados por blocks estruturados (ver `../slots`).
   * Injetados aqui para que `core/` não precise importar de `insights/`.
   */
  slots?: ChatSlots;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  // Usa o hook reativo para que o guardião funcione corretamente em modo demo
  // (/demo/ia-actions-center) e em testes (jsdom inicia com pathname = '/').
  const routes = useActiveRoutes();
  // No Centro de ações com IA o chat É a página (PED-12262, Fase 2):
  // a bolha flutuante não deve ser montada para não duplicar a sessão.
  const isActionCenterRoute = pathname.startsWith(routes.ACTION_CENTER);
  const [pendingMessage, setPendingMessage] =
    useState<PendingChatMessage | null>(null);

  // Evento `bruna_aberta` (PED-14256): uma vez por abertura do chat, com a tela de origem.
  // Cobre open/openWithMessage/toggle/onOpenChange, que convergem em `isOpen`.
  const trackBrunaOpened = useTrackBrunaOpened();
  const openRegisteredRef = useRef(false);
  // Só a abertura mais recente fica pendente (usuário ainda não hidratou): abrir/fechar
  // várias vezes antes do `userId` chegar envia um único evento, com a última tela.
  const pendingScreenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      openRegisteredRef.current = false;
    } else if (!openRegisteredRef.current) {
      openRegisteredRef.current = true;
      // `pathname` precisa estar nas deps: é lido aqui, no momento da abertura.
      pendingScreenRef.current = pathname;
    }
    // Enviada quando `userId` chegar, mesmo com o chat já fechado.
    if (
      pendingScreenRef.current !== null &&
      trackBrunaOpened(pendingScreenRef.current)
    ) {
      pendingScreenRef.current = null;
    }
  }, [isOpen, pathname, trackBrunaOpened]);

  const open = useCallback(() => {
    setIsOpen(true);
    saveChatState(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);
  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (open) saveChatState(true);
  }, []);
  const toggle = useCallback(() => setIsOpen((v: boolean) => !v), []);

  const openWithMessage = useCallback(
    (
      messageToSend: string,
      displayText?: string,
      attachment?: { fileName: string; fileSize: number },
    ) => {
      // Quando abrir com mensagem guiada, deve focar na mensagem enviada.
      markScrolledToStart();
      setPendingMessage({ messageToSend, displayText, attachment });
      setIsOpen(true);
      // Mesma persistência de `open()`/`handleOpenChange()`: sem isto o chat
      // abre mas `hasChatState()` segue falso, afetando o scroll inicial.
      saveChatState(true);
    },
    [],
  );

  const clearPendingMessage = useCallback(() => setPendingMessage(null), []);

  const [chatMessagesUpdater, setChatMessagesUpdater] = useState<Dispatch<
    SetStateAction<Message[]>
  > | null>(null);

  const registerChatMessagesUpdater = useCallback(
    (updater: Dispatch<SetStateAction<Message[]>> | null) => {
      setChatMessagesUpdater(() => updater);
    },
    [],
  );
  const [activeAgent, setActiveAgent] = useState<AgentType | null>(null);
  const [forcedAgent, setForcedAgent] = useState<AgentType | null>(null);

  return (
    <ChatSlotsProvider slots={slots}>
      <ChatbotContext.Provider
        value={{
          isOpen,
          open,
          close,
          toggle,
          openWithMessage,
          pendingMessage,
          clearPendingMessage,
          updateChatMessages: chatMessagesUpdater,
          registerChatMessagesUpdater,
          activeAgent,
          setActiveAgent,
          forcedAgent,
          setForcedAgent,
        }}
      >
        {host === "docked" && !isActionCenterRoute ? (
          // Painel docked: conteúdo e painel dividem a largura (flex row), o
          // conteúdo comprime quando o painel abre. O painel fica fora do Outlet,
          // então a conversa continua ao navegar entre telas.
          <div className="flex h-full min-h-0 w-full flex-1 items-stretch overflow-hidden">
            <div className="h-full min-w-0 flex-1">{children}</div>
            <AgentSidePanel isOpen={isOpen} onOpenChange={handleOpenChange} />
          </div>
        ) : (
          <>
            {children}
            {host === "floating" && !isActionCenterRoute && (
              <FloatingChatbot
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
              />
            )}
          </>
        )}
      </ChatbotContext.Provider>
    </ChatSlotsProvider>
  );
}

export function useChatbot(): ChatbotContextValue {
  const ctx = useContext(ChatbotContext);
  // Retorna valor padrão quando context é null (ex.: durante HMR do Vite, quando
  // um componente é re-renderizado antes do ChatbotProvider reaplicar o valor).
  // Evita "useChatbot deve ser usado dentro de <ChatbotProvider>" ao editar arquivos.
  return ctx ?? DEFAULT_CHATBOT_CONTEXT;
}
